// Modules
import styled, { keyframes } from "styled-components"
import { rgba } from "polished"

// Constants
import colors from "~constants/colors"
import grid from "~constants/grid"

export const Form = styled.form`
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: 10px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const FormInputBase = styled.input`
  appearance: none;
  border-radius: 5px;
  border: none;
  font-size: 16px;
  padding: 7px 13px;

  &:focus {
    outline: 0;
  }
`

const fadeInContent = keyframes`
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const FormSubmit = styled.button`
  align-items: center;
  background: linear-gradient(
    135deg,
    ${colors.lightBlue1} 0%,
    ${colors.cobalt} 100%
  );
  background-size: 140% 140%;
  border-radius: 12px;
  border: none;
  box-shadow: 0 8px 20px ${rgba(colors.cobalt, 0.28)};
  color: white;
  cursor: pointer;
  display: inline-flex;
  font-size: 15px;
  font-weight: 600;
  gap: 10px;
  height: 50px;
  justify-content: center;
  letter-spacing: 0.2px;
  margin-top: 8px;
  padding: 7px 13px;
  transition:
    box-shadow 160ms ease,
    transform 120ms ease,
    filter 160ms ease,
    background-position 400ms ease;

  > * {
    animation: ${fadeInContent} 220ms ease both;
  }

  .loading-icon {
    color: white;
  }

  &:hover:not(:disabled) {
    box-shadow: 0 12px 26px ${rgba(colors.cobalt, 0.4)};
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    box-shadow: 0 4px 12px ${rgba(colors.cobalt, 0.32)};
    transform: translateY(0);
  }

  /* Keep the button looking alive while submitting */
  &.loading {
    background-position: 100% 50%;
    cursor: progress;
    opacity: 1;
  }

  &:disabled:not(.loading) {
    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.55;
  }
`

export const FormViewWrapper = styled.div`
  background-color: white;
  border: 1px solid ${colors.gray9};
  border-radius: 12px;
  overflow: hidden;

  &.with-padding {
    padding: 30px;
  }
`

export const FormViewContainer = styled.div`
  background-color: white;
  padding: 30px;
`

export const FormViewHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${colors.gray2};
  display: flex;
  margin-bottom: 30px;
  padding-bottom: 15px;

  > span:first-child {
    margin-right: 15px;
  }

  > figure {
    height: 60px;
    margin: 0 15px 0 0;
    min-width: 60px;
    width: 60px;

    > img {
      height: 100%;
      margin: 0;
      object-fit: scale-down;
    }
  }

  > h2 {
    color: ${colors.gray6};
    font-weight: 500;
    font-size: 1.2rem;
    margin: 0;
  }
`

export const FormViewBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;

  &:not(:last-of-type) {
    margin-bottom: 20px;
  }

  > h3 {
    font-size: 1.2rem;
    font-weight: 500;
    margin-bottom: 10px;
  }

  > p {
    margin: 0 0 10px 0;
  }

  > ul {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    list-style-type: none;
    margin: 0;
    padding: 0;

    > li {
      margin: 0 0 15px 0;
      padding-right: 20px;
      width: 50%;

      > span {
        display: block;
        font-weight: 500;
      }
    }
  }

  @media (min-width: ${grid.large}) {
    &:not(:last-of-type) {
      margin-bottom: 30px;
    }

    > p {
      margin: 0;
    }

    > ul {
      flex-wrap: nowrap;

      > li {
        margin: 0;
        width: 25%;
      }
    }
`

export const FormViewCoverImage = styled.figure`
  height: 200px;
  width: calc(100% + 60px);
  .react-loading-skeleton {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    position: relative;
    top: -5px;
  }
`

export const FormViewTitle = styled.div`
  align-items: center;
  display: flex;
  height: 76px;

  > span:not(:last-child) {
    margin-right: 15px;
  }

  > figure {
    height: 60px;
    margin: 0 15px 0 0;
    min-width: 60px;
    width: 60px;

    > img {
      height: 100%;
      margin: 0;
      object-fit: scale-down;
    }
  }

  > h2 {
    color: ${colors.gray6};
    font-weight: 500;
    font-size: 1.2rem;
    margin: 0;
  }

  .skeleton-status {
    border-radius: 50%;
    line-height: 11px;
    width: 11px;
  }
`
